import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getReport } from '../reports/api';
import type { Report } from '../reports/types';
import { removeAllowanceLine } from './api';

/** The problems a removal names in words; anything else reads as «riprova». */
const KNOWN = [
  'ALLOWANCE_REASON_REQUIRED',
  'MONTH_CLOSED',
  'REPORT_APPROVED',
  'REPORT_FORBIDDEN',
  'REPORT_NOT_FOUND',
  'EXPENSE_REJECTED',
  'ALLOWANCE_LINE_NOT_FOUND',
] as const;

/** The refusals that mean the note moved on while the request ran: the page reads it again. */
const STALE: readonly AllowanceError[] = [
  'REPORT_APPROVED',
  'REPORT_FORBIDDEN',
  'REPORT_NOT_FOUND',
  'EXPENSE_REJECTED',
  'ALLOWANCE_LINE_NOT_FOUND',
];
export type AllowanceError = (typeof KNOWN)[number] | 'other';

const errorOf = (code: string): AllowanceError =>
  (KNOWN as readonly string[]).includes(code) ? (code as AllowanceError) : 'other';

/**
 * Decision 203: the system manages a Trasferta's flat-rate lines, and Gestione or an approver
 * takes one out with a reason (`can.removeAllowanceLine`); the note is read anew after it, since
 * its totals and rows change.
 */
export function useAllowance(report: Report, adopt: (report: Report) => void, reload: () => void) {
  const { t } = useTranslation('expenses');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<AllowanceError | null>(null);

  const refresh = async (): Promise<void> => {
    const next = await getReport(report.id);
    if (next.ok) adopt(next.value);
    else reload();
  };

  return {
    busy,
    error,
    /** Forgets the last refusal, when the dialog closes or opens on another line. */
    dismiss: () => setError(null),
    /** Whether the caller may take a line out of this note: never its owner. */
    removes: report.can?.removeAllowanceLine === true,
    /** Takes the line out with `reason`; true when it went, so the dialog closes. */
    remove: async (expenseId: string, reason: string): Promise<boolean> => {
      setBusy(true);
      setError(null);
      const outcome = await removeAllowanceLine(report.id, expenseId, reason);
      if (outcome.ok) {
        await refresh();
        toast.success(t('flatRate.removed'));
      } else {
        const problem = errorOf(outcome.code);
        setError(problem);
        // The note moved on (approved, deleted, the line went): show it as it is now.
        if (STALE.includes(problem)) await refresh();
      }
      setBusy(false);
      return outcome.ok;
    },
  } as const;
}
